import {
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  ChangeDetectorRef,
  HostListener,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoPipe } from '@jsverse/transloco';

type ClockFaceMode = 'hour' | 'minute';

/**
 * Material-style clock time picker (12h dial + AM/PM), themed with app CSS vars.
 * Digital HH/MM fields are keyboard-editable. Outputs a 24h Date on confirm / live change.
 */
@Component({
  selector: 'app-time-picker-panel',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, TranslocoPipe],
  templateUrl: './time-picker-panel.component.html',
  styleUrl: './time-picker-panel.component.scss',
})
export class TimePickerPanelComponent {
  private readonly cdr = inject(ChangeDetectorRef);

  private _initialTime: Date | null = null;
  private _hour12 = 12;
  private _isPm = false;
  private _minute = 0;
  private _mode: ClockFaceMode = 'hour';
  private _dragging = false;

  /** Draft text while typing in digital fields (null = show padded value). */
  hourDraft: string | null = null;
  minuteDraft: string | null = null;

  /** Clock face radius for number placement (CSS px). */
  readonly dialRadius = 96;
  readonly hours = Array.from({ length: 12 }, (_, i) => i + 1);
  /** Minute labels around the dial (Material-style 5-minute steps). */
  readonly minuteMarks = Array.from({ length: 12 }, (_, i) => i * 5);

  @ViewChild('hourInput') hourInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('minuteInput') minuteInputRef?: ElementRef<HTMLInputElement>;

  @Input()
  get initialTime(): Date {
    return this._initialTime ?? new Date();
  }
  set initialTime(value: Date) {
    this._initialTime = value ?? new Date();
    this.applyInitialTime();
    this.hourDraft = null;
    this.minuteDraft = null;
    this.cdr.markForCheck();
  }

  /** Inclusive lower bound for the combined date+time (same calendar day as initialTime). */
  @Input() minDate: Date | null = null;
  /** Inclusive upper bound for the combined date+time. */
  @Input() maxDate: Date | null = null;

  @Output() timeSelected = new EventEmitter<Date>();
  @Output() timeChanged = new EventEmitter<Date>();
  @Output() cancelled = new EventEmitter<void>();

  get hour12(): number {
    return this._hour12;
  }
  get isPm(): boolean {
    return this._isPm;
  }
  get minute(): number {
    return this._minute;
  }
  get mode(): ClockFaceMode {
    return this._mode;
  }
  get displayHour(): string {
    return this.hourDraft ?? String(this._hour12).padStart(2, '0');
  }
  get displayMinute(): string {
    return this.minuteDraft ?? this._minute.toString().padStart(2, '0');
  }

  /** Angle of the clock hand in degrees (0 = 12 o'clock). */
  get handAngleDeg(): number {
    if (this._mode === 'hour') {
      return (this._hour12 % 12) * 30;
    }
    return this._minute * 6;
  }

  dialNumberStyle(value: number, kind: ClockFaceMode): Record<string, string> {
    const index =
      kind === 'hour' ? value % 12 : Math.round(value / 5) % 12;
    const angleDeg = index * 30 - 90;
    const rad = (angleDeg * Math.PI) / 180;
    const x = Math.cos(rad) * this.dialRadius;
    const y = Math.sin(rad) * this.dialRadius;
    return {
      transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
    };
  }

  isHourSelected(h: number): boolean {
    return this._mode === 'hour' && this._hour12 === h;
  }

  isMinuteSelected(m: number): boolean {
    return this._mode === 'minute' && this._minute === m;
  }

  /**
   * Hour is disabled only if no minute in that hour can satisfy min/max
   * (e.g. min 14:30 → 2 PM stays enabled; minutes &lt; 30 are disabled).
   */
  isHourDisabled(h: number): boolean {
    const atStart = this.buildDateWith(h, this._isPm, 0).getTime();
    const atEnd = this.buildDateWith(h, this._isPm, 59).getTime();
    const minT = this.minDate?.getTime() ?? Number.NEGATIVE_INFINITY;
    const maxT = this.maxDate?.getTime() ?? Number.POSITIVE_INFINITY;
    return atEnd < minT || atStart > maxT;
  }

  isMinuteDisabled(m: number): boolean {
    return !this.isDateWithinBounds(
      this.buildDateWith(this._hour12, this._isPm, m)
    );
  }

  isAmDisabled(): boolean {
    // AM is usable if any hour/minute in 00:00–11:59 is within bounds.
    for (let h = 1; h <= 12; h += 1) {
      for (const minute of [0, 59]) {
        if (this.isDateWithinBounds(this.buildDateWith(h, false, minute))) {
          return false;
        }
      }
    }
    return true;
  }

  isPmDisabled(): boolean {
    for (let h = 1; h <= 12; h += 1) {
      for (const minute of [0, 59]) {
        if (this.isDateWithinBounds(this.buildDateWith(h, true, minute))) {
          return false;
        }
      }
    }
    return true;
  }

  /**
   * First mousedown on an unfocused field: focus + select immediately.
   * Without this, the browser places a caret and Angular's [value] rebind
   * clears selection — so editing felt like it needed a second click.
   */
  onDigitMouseDown(event: MouseEvent): void {
    const input = event.target as HTMLInputElement | null;
    if (!input || document.activeElement === input) return;
    event.preventDefault();
    input.focus();
    input.select();
  }

  onHourFocus(event: FocusEvent): void {
    this._mode = 'hour';
    // Keep padded value so [value] does not rewrite the DOM on focus.
    this.hourDraft = String(this._hour12).padStart(2, '0');
    this.cdr.detectChanges();
    (event.target as HTMLInputElement)?.select();
  }

  onMinuteFocus(event: FocusEvent): void {
    this._mode = 'minute';
    this.minuteDraft = String(this._minute).padStart(2, '0');
    this.cdr.detectChanges();
    (event.target as HTMLInputElement)?.select();
  }

  onHourInput(raw: string): void {
    const digits = raw.replace(/\D/g, '').slice(0, 2);
    this.hourDraft = digits;
    if (digits === '') {
      this.cdr.markForCheck();
      return;
    }
    const n = Number(digits);
    if (!Number.isFinite(n)) {
      this.cdr.markForCheck();
      return;
    }
    // Commit when 2 digits typed, or when value can no longer grow (e.g. 3–9).
    if (digits.length === 2 || n > 1) {
      const hour = n === 0 ? 12 : Math.min(12, Math.max(1, n));
      this._hour12 = hour;
      this.hourDraft = String(hour);
      this.emitTimeChange();
      if (digits.length === 2 || n > 9) {
        this.focusMinuteField();
      }
    }
    this.cdr.markForCheck();
  }

  onMinuteInput(raw: string): void {
    const digits = raw.replace(/\D/g, '').slice(0, 2);
    this.minuteDraft = digits;
    if (digits === '') {
      this.cdr.markForCheck();
      return;
    }
    const n = Number(digits);
    if (!Number.isFinite(n)) {
      this.cdr.markForCheck();
      return;
    }
    if (digits.length === 2 || n > 5) {
      const minute = Math.min(59, Math.max(0, n));
      this._minute = minute;
      this.minuteDraft = String(minute).padStart(2, '0');
      this.emitTimeChange();
    }
    this.cdr.markForCheck();
  }

  onHourBlur(): void {
    this.commitHourDraft();
    this.writeHourInput();
    this.cdr.markForCheck();
  }

  onMinuteBlur(): void {
    this.commitMinuteDraft();
    this.writeMinuteInput();
    this.cdr.markForCheck();
  }

  onHourKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowRight' || event.key === ':' || event.key === 'Enter') {
      event.preventDefault();
      this.commitHourDraft();
      this.focusMinuteField();
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.setHour(this._hour12 === 12 ? 1 : this._hour12 + 1, false);
      this.hourDraft = String(this._hour12);
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.setHour(this._hour12 === 1 ? 12 : this._hour12 - 1, false);
      this.hourDraft = String(this._hour12);
    }
  }

  onMinuteKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.commitMinuteDraft();
      this.focusHourField();
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      this.commitMinuteDraft();
      this.confirm();
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.setMinute((this._minute + 1) % 60);
      this.minuteDraft = String(this._minute).padStart(2, '0');
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.setMinute((this._minute + 59) % 60);
      this.minuteDraft = String(this._minute).padStart(2, '0');
    }
  }

  setHour(h: number, advanceToMinutes = true): void {
    if (this.isHourDisabled(h)) return;
    this._hour12 = h;
    this.hourDraft = null;
    this.clampCurrentToBounds();
    this.cdr.markForCheck();
    this.emitTimeChange();
    if (advanceToMinutes) {
      this._mode = 'minute';
    }
  }

  setMinute(m: number): void {
    if (this.isMinuteDisabled(m)) return;
    this._minute = Math.max(0, Math.min(59, m));
    this.minuteDraft = null;
    this.clampCurrentToBounds();
    this.cdr.markForCheck();
    this.emitTimeChange();
  }

  setAmPm(pm: boolean): void {
    if (pm ? this.isPmDisabled() : this.isAmDisabled()) return;
    this._isPm = pm;
    this.clampCurrentToBounds();
    this.cdr.markForCheck();
    this.emitTimeChange();
  }

  onDialPointerDown(event: PointerEvent): void {
    const target = event.currentTarget as HTMLElement | null;
    if (!target) return;
    target.setPointerCapture?.(event.pointerId);
    this._dragging = true;
    this.applyDialPointer(event, target);
  }

  onDialPointerMove(event: PointerEvent): void {
    if (!this._dragging) return;
    const target = event.currentTarget as HTMLElement | null;
    if (!target) return;
    this.applyDialPointer(event, target);
  }

  onDialPointerUp(event: PointerEvent): void {
    if (!this._dragging) return;
    this._dragging = false;
    const target = event.currentTarget as HTMLElement | null;
    target?.releasePointerCapture?.(event.pointerId);
    if (this._mode === 'hour') {
      this._mode = 'minute';
      this.cdr.markForCheck();
    }
  }

  @HostListener('document:pointerup')
  onDocumentPointerUp(): void {
    this._dragging = false;
  }

  confirm(): void {
    this.commitHourDraft();
    this.commitMinuteDraft();
    this.clampCurrentToBounds();
    this.timeSelected.emit(this.buildCurrentDate());
  }

  cancelClick(): void {
    this.cancelled.emit();
  }

  private focusMinuteField(): void {
    this._mode = 'minute';
    this.cdr.markForCheck();
    queueMicrotask(() => {
      const el = this.minuteInputRef?.nativeElement;
      el?.focus();
      el?.select();
    });
  }

  private focusHourField(): void {
    this._mode = 'hour';
    this.cdr.markForCheck();
    queueMicrotask(() => {
      const el = this.hourInputRef?.nativeElement;
      el?.focus();
      el?.select();
    });
  }

  private commitHourDraft(): void {
    if (this.hourDraft == null) return;
    const digits = this.hourDraft.replace(/\D/g, '');
    if (digits !== '') {
      const n = Number(digits);
      this._hour12 =
        !Number.isFinite(n) || n === 0 ? 12 : Math.min(12, Math.max(1, n));
      this.clampCurrentToBounds();
      this.emitTimeChange();
    }
    this.hourDraft = null;
  }

  private commitMinuteDraft(): void {
    if (this.minuteDraft == null) return;
    const digits = this.minuteDraft.replace(/\D/g, '');
    if (digits !== '') {
      const n = Number(digits);
      this._minute = !Number.isFinite(n) ? 0 : Math.min(59, Math.max(0, n));
      this.clampCurrentToBounds();
      this.emitTimeChange();
    }
    this.minuteDraft = null;
  }

  /** Force DOM value — `[value]` often skips rewrite after user typing. */
  private writeHourInput(): void {
    const el = this.hourInputRef?.nativeElement;
    if (!el) return;
    el.value = String(this._hour12).padStart(2, '0');
  }

  private writeMinuteInput(): void {
    const el = this.minuteInputRef?.nativeElement;
    if (!el) return;
    el.value = String(this._minute).padStart(2, '0');
  }

  private applyDialPointer(event: PointerEvent, dialEl: HTMLElement): void {
    const rect = dialEl.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = event.clientX - cx;
    const dy = event.clientY - cy;
    let angle = (Math.atan2(dy, dx) * 180) / Math.PI + 90;
    if (angle < 0) angle += 360;

    if (this._mode === 'hour') {
      let hour = Math.round(angle / 30) % 12;
      if (hour === 0) hour = 12;
      if (this.isHourDisabled(hour)) {
        return;
      }
      if (hour !== this._hour12) {
        this._hour12 = hour;
        this.hourDraft = null;
        this.clampCurrentToBounds();
        this.cdr.markForCheck();
        this.emitTimeChange();
      }
      return;
    }

    const minute = Math.round(angle / 6) % 60;
    if (this.isMinuteDisabled(minute)) {
      return;
    }
    if (minute !== this._minute) {
      this._minute = minute;
      this.minuteDraft = null;
      this.clampCurrentToBounds();
      this.cdr.markForCheck();
      this.emitTimeChange();
    }
  }

  private buildCurrentDate(): Date {
    return this.buildDateWith(this._hour12, this._isPm, this._minute);
  }

  private buildDateWith(hour12: number, isPm: boolean, minute: number): Date {
    const d = new Date((this._initialTime ?? new Date()).getTime());
    const hour24 = isPm
      ? hour12 === 12
        ? 12
        : hour12 + 12
      : hour12 === 12
        ? 0
        : hour12;
    d.setHours(hour24, minute, 0, 0);
    return d;
  }

  private isDateWithinBounds(date: Date): boolean {
    const t = date.getTime();
    if (this.minDate && t < this.minDate.getTime()) {
      return false;
    }
    if (this.maxDate && t > this.maxDate.getTime()) {
      return false;
    }
    return true;
  }

  private clampCurrentToBounds(): void {
    const built = this.buildCurrentDate();
    if (this.isDateWithinBounds(built)) {
      return;
    }
    let target = built;
    if (this.minDate && built.getTime() < this.minDate.getTime()) {
      target = new Date(this.minDate.getTime());
    }
    if (this.maxDate && target.getTime() > this.maxDate.getTime()) {
      target = new Date(this.maxDate.getTime());
    }
    this.applyTimeFromDate(target);
  }

  private emitTimeChange(): void {
    this.timeChanged.emit(this.buildCurrentDate());
  }

  private applyInitialTime(): void {
    if (!this._initialTime) return;
    let d = new Date(this._initialTime.getTime());
    if (this.minDate && d.getTime() < this.minDate.getTime()) {
      d = new Date(this.minDate.getTime());
    }
    if (this.maxDate && d.getTime() > this.maxDate.getTime()) {
      d = new Date(this.maxDate.getTime());
    }
    this.applyTimeFromDate(d);
    this._mode = 'hour';
  }

  private applyTimeFromDate(d: Date): void {
    const h24 = d.getHours();
    this._minute = d.getMinutes();
    if (h24 === 0) {
      this._hour12 = 12;
      this._isPm = false;
    } else if (h24 < 12) {
      this._hour12 = h24;
      this._isPm = false;
    } else if (h24 === 12) {
      this._hour12 = 12;
      this._isPm = true;
    } else {
      this._hour12 = h24 - 12;
      this._isPm = true;
    }
  }
}
